import { apiClient, handleApiError } from '@/lib/api-client';
import { queryClient } from '@/lib/query-client';
import { useSetupStore } from '@/store/setupStore';
import type { SignInFormData, CompleteRegistrationData } from '@/lib/validation';

export interface SetupConfig {
  currentStep?: number;
  selectedFeatures?: string[];
  selectedNameDetails?: string[];
  uniqueId?: { kind: 'klaviyo_id' | 'email' | 'custom'; fieldName: string };
  selectedDataInputs?: string[];
  fieldNameOverrides?: Record<string, string>;
  emailLocalPartOnly?: boolean;
  /** U-08: user-added extra field mappings (canonical key → platform field name). */
  extraFields?: { key: string; fieldName: string }[];
  selectedReturnMethod?: 'csv' | 'api';
  primaryIdentifier?: 'klaviyo_id' | 'email' | 'uid';
  payloadConfig?: {
    selectedFields?: string[];
    varOverrides?: Record<string, string>;
  };
  resultMode?: 'none' | 'green' | 'green_yellow' | 'all';
  klaviyoApiKey?: string;
  klaviyoConnected?: boolean;
  /** Last 4 characters of the saved Klaviyo key (server-set, never the key itself). */
  klaviyoApiKeyHint?: string;
  /** Name-optimization wizard finished at least once (drives the setup hub status). */
  nameOptimizationComplete?: boolean;
  emailDomainEnabled?: boolean;
  emailDomainFields?: string[];
  locationEnabled?: boolean;
}

export type TwoFaMethod = 'totp' | 'email';

export interface User {
  client_id: string;
  email: string;
  firstName: string;
  lastName: string;
  companyName: string;
  companyWebsite?: string;
  phoneNumber?: string;
  emailMarketingTool: string;
  /** Granular registered platform (e.g. 'brevo'); drives the F-23 AI prompt. */
  emailPlatform?: string;
  createdAt: string;
  setupComplete: boolean;
  setupConfig?: SetupConfig;
  /** U-14: one-time finish-setup gift state ('available' | 'used' | undefined). */
  firstBatchDiscount?: 'available' | 'used' | null;
  totp_enabled?: boolean;
  totp_method?: TwoFaMethod | null;
  /** Operator flag from /auth/me; gates the Admin nav group and routes. */
  isAdmin?: boolean;
}

export interface AuthResponse {
  user: User;
  token: string;
  expiresIn: number;
  totp_required?: boolean;
  method?: TwoFaMethod;
  temp_token?: string;
  requires2FASetup?: boolean;
}

export interface RegisterResponse {
  requires_email_verification: boolean;
  email_sent: boolean;
  email: string;
}

export interface ResendVerificationResponse {
  ok: boolean;
  message: string;
}

export interface TotpSetupResponse {
  secret: string;
  provisioning_uri: string;
}

export interface TotpVerifyResponse {
  message: string;
  subscription_status?: string;
}

class AuthService {
  async signIn(credentials: SignInFormData): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<AuthResponse>('/api/v1/auth/login/email', credentials);
      const data = response.data;

      // If 2FA is required, don't set full auth data
      if (data.totp_required) {
        return data;
      }

      // Fresh session: clear stale query cache and reset setup wizard state
      queryClient.clear();
      useSetupStore.getState().resetSetup();
      return data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async register(
    data: CompleteRegistrationData & { emailPlatform?: string; emailMarketingTool?: string },
  ): Promise<RegisterResponse> {
    try {
      const platform = data.emailPlatform || '';
      const payload = {
        email: data.email,
        password: data.password,
        company_name: data.companyName,
        first_name: data.firstName,
        last_name: data.lastName,
        email_marketing_tool: data.emailMarketingTool || (platform === 'klaviyo' ? 'klaviyo' : 'other'),
        email_platform: platform,
        company_website: data.companyWebsite || '',
        phone_number: data.phoneNumber || '',
        // F4 (audit 2026-05-12): country drives backend VAT rate selection.
        country: (data as { country?: string }).country || 'DE',
        // Legal consents captured at registration (AGB / Datenschutz / AVV)
        // plus the optional marketing opt-in.
        agreed_to_terms: data.agreedToTerms,
        agreed_to_privacy: data.agreedToPrivacy,
        agreed_to_dpa: (data as { agreedToDpa?: boolean }).agreedToDpa ?? false,
        marketing_opt_in: (data as { marketingOptIn?: boolean }).marketingOptIn ?? false,
      };
      const response = await apiClient.post<RegisterResponse>('/api/v1/auth/register', payload);
      queryClient.clear();
      useSetupStore.getState().resetSetup();
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async resendVerification(email: string): Promise<ResendVerificationResponse> {
    try {
      const response = await apiClient.post<ResendVerificationResponse>(
        '/api/v1/auth/resend-verification',
        { email },
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async requestPasswordReset(email: string): Promise<{ ok: boolean; message: string }> {
    try {
      const response = await apiClient.post<{ ok: boolean; message: string }>(
        '/api/v1/auth/password-reset/request',
        { email },
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async confirmPasswordReset(token: string, newPassword: string): Promise<{ ok: boolean; message: string }> {
    try {
      const response = await apiClient.post<{ ok: boolean; message: string }>(
        '/api/v1/auth/password-reset/confirm',
        { token, new_password: newPassword },
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async getCurrentUser(): Promise<User> {
    try {
      const response = await apiClient.get<User>('/api/v1/auth/me');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /** `currentCode` proves the CURRENT factor; required when 2FA is already enabled. */
  async setup2FA(currentCode?: string): Promise<TotpSetupResponse> {
    try {
      const response = await apiClient.post<TotpSetupResponse>(
        '/api/v1/2fa/setup',
        currentCode ? { current_code: currentCode } : {},
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async verifySetup2FA(code: string): Promise<TotpVerifyResponse> {
    try {
      const response = await apiClient.post<TotpVerifyResponse>('/api/v1/2fa/verify-setup', { code });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /** Starts (or, while pending, resends) email 2FA enrollment. `currentCode`
   *  proves the CURRENT factor when 2FA is already enabled. */
  async setupEmail2FA(currentCode?: string): Promise<{ message: string; method: 'email' }> {
    try {
      const response = await apiClient.post<{ message: string; method: 'email' }>(
        '/api/v1/2fa/setup/email',
        currentCode ? { current_code: currentCode } : {},
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async verifyEmail2FASetup(code: string): Promise<TotpVerifyResponse> {
    try {
      const response = await apiClient.post<TotpVerifyResponse>(
        '/api/v1/2fa/verify-setup/email',
        { code },
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /** Signed-in user: email a code for the CURRENT email factor (proof before
   *  changing or disabling 2FA). No-op for authenticator-app accounts. */
  async sendCurrent2FACode(): Promise<{ ok: boolean }> {
    try {
      const response = await apiClient.post<{ ok: boolean }>('/api/v1/2fa/send-code', {});
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async send2FALoginCode(tempToken: string): Promise<{ ok: boolean }> {
    try {
      const response = await apiClient.post<{ ok: boolean }>(
        '/api/v1/2fa/login/send-code',
        {},
        { headers: { Authorization: `Bearer ${tempToken}` } },
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async verifyTotp(code: string, tempToken: string): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<AuthResponse>('/api/v1/2fa/verify', { code }, {
        headers: { Authorization: `Bearer ${tempToken}` },
      });
      queryClient.clear();
      useSetupStore.getState().resetSetup();
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async disable2FA(code: string): Promise<TotpVerifyResponse> {
    try {
      const response = await apiClient.post<TotpVerifyResponse>('/api/v1/2fa/disable', { code });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /** Persist the wizard config; resolves with the stored (secret-free) config. */
  async saveSetupConfig(config: SetupConfig): Promise<SetupConfig> {
    try {
      const { data } = await apiClient.put<SetupConfig>('/api/v1/setup/config', config);
      return data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async completeSetup(): Promise<void> {
    try {
      await apiClient.post('/api/v1/setup/complete');
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async signOut(): Promise<void> {
    try {
      await apiClient.post('/api/v1/auth/logout');
    } catch {
      // Best-effort — cookie may already be expired
    }
    queryClient.clear();
    useSetupStore.getState().resetSetup();
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      await this.getCurrentUser();
      return true;
    } catch {
      return false;
    }
  }
}

export const authService = new AuthService();
